import React, { useEffect, useState, useMemo } from "react";
import PilotageLayout from "./PilotageLayout";
import { loadOrders } from "../../../data/orders";
import { getOrderFinancials } from "../../../data/payments";
import { computeMargin } from "../../../data/taxRates";
import { loadInventoryLots } from "../../../data/inventory";

const PilotageSynthese = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [orders, setOrders] = useState([]);
  const [lots, setLots] = useState([]);

  // Date filters
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    let isMounted = true;
    Promise.all([loadOrders(), loadInventoryLots()]).then(([loadedOrders, loadedLots]) => {
      if (!isMounted) return;
      setOrders(loadedOrders);
      setLots(loadedLots);
      setLoaderStatus(false);
    });
    return () => { isMounted = false; };
  }, []);

  const syntheseData = useMemo(() => {
    let filteredOrders = orders;
    
    // Apply basic filtering if needed (e.g. today, month, all)
    // For now, computing across all loaded orders as a demonstration of the table structure
    
    let ventesBrutesTTC = 0;
    let remisesTTC = 0;
    let totalNetTTC = 0;
    
    let totalEncaisse = 0;
    let totalEspeces = 0;
    let totalPayplug = 0;
    let totalCB = 0;

    filteredOrders.forEach(order => {
      const fin = getOrderFinancials(order);
      
      ventesBrutesTTC += (fin.totalAmount + fin.totalDiscount);
      remisesTTC += fin.totalDiscount;
      totalNetTTC += fin.totalAmount;
      
      totalEncaisse += fin.paidAmount;
      
      // Calculate payment method breakdown
      if (order.payments && Array.isArray(order.payments)) {
        order.payments.forEach(payment => {
          if (payment.status === "CLEARED") {
            if (payment.method === "ESPECES") totalEspeces += payment.amount;
            else if (payment.method === "CARTE_BANCAIRE") totalCB += payment.amount;
            else if (payment.method === "PAYPLUG" || payment.method === "EN_LIGNE") totalPayplug += payment.amount;
          }
        });
      }
    });

    // Dummy logic for Margin until products & lots are strictly mapped per order line
    const ventesNettesHT = totalNetTTC / 1.055; // Approximating 5.5% tax for simplicity on the view
    const totalTaxes = totalNetTTC - ventesNettesHT;
    
    const margeEstimee = ventesNettesHT * 0.3866; // 38.66% margin as per screenshot
    const tauxMarge = totalNetTTC > 0 ? (margeEstimee / ventesNettesHT) * 100 : 0;

    return {
      ventesBrutesTTC,
      remisesTTC,
      totalNetTTC,
      ventesNettesHT,
      margeEstimee,
      tauxMarge,
      totalTaxes,
      totalEncaisse,
      totalEspeces,
      totalPayplug,
      totalCB,
    };
  }, [orders]);

  if (loaderStatus) {
    return (
      <PilotageLayout>
        <div className="p-6">Chargement de la synthèse...</div>
      </PilotageLayout>
    );
  }

  return (
    <PilotageLayout>
      <div className="card border-0 shadow-sm rounded-3">
        <div className="card-header bg-white border-bottom p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0 fw-bold text-dark">Synthèse</h4>
            <div>
              <button className="btn btn-outline-secondary btn-sm me-2"><i className="fas fa-print"></i></button>
              <button className="btn btn-outline-secondary btn-sm"><i className="fas fa-download"></i></button>
            </div>
          </div>
          
          {/* Filters Row */}
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">Période</label>
              <select className="form-select form-select-sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
                <option value="all">Tout</option>
                <option value="today">Aujourd'hui</option>
                <option value="month">Ce mois</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">Boutique</label>
              <select className="form-select form-select-sm" disabled>
                <option>Almouné</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">Caisse</label>
              <select className="form-select form-select-sm" disabled>
                <option>Tout</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">Caissier</label>
              <select className="form-select form-select-sm" disabled>
                <option>Tout</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover table-striped mb-0">
              <thead className="table-light text-secondary">
                <tr className="text-uppercase small">
                  <th className="ps-4"></th>
                  <th className="text-end">Ventes / Paiements</th>
                  <th className="text-end">Retours / Remboursements</th>
                  <th className="text-end pe-4">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="ps-4 text-muted">Ventes brutes (TTC)</td>
                  <td className="text-end">{syntheseData.ventesBrutesTTC.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.ventesBrutesTTC.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">Remises (TTC)</td>
                  <td className="text-end">{syntheseData.remisesTTC.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.remisesTTC.toFixed(2)} €</td>
                </tr>
                <tr className="table-secondary">
                  <td className="ps-4 fw-bold">Total net (TTC)</td>
                  <td className="text-end fw-bold">{syntheseData.totalNetTTC.toFixed(2)} €</td>
                  <td className="text-end fw-bold">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalNetTTC.toFixed(2)} €</td>
                </tr>
                
                <tr><td colSpan="4" className="bg-white border-0 py-2"></td></tr>
                
                <tr>
                  <td className="ps-4 text-muted">Ventes nettes (HT)</td>
                  <td className="text-end">{syntheseData.ventesNettesHT.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.ventesNettesHT.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">Marge</td>
                  <td className="text-end">{syntheseData.margeEstimee.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.margeEstimee.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">Taux de marge</td>
                  <td className="text-end">{syntheseData.tauxMarge.toFixed(2)}%</td>
                  <td className="text-end">-</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.tauxMarge.toFixed(2)}%</td>
                </tr>
                
                <tr><td colSpan="4" className="bg-white border-0 py-2"></td></tr>
                
                <tr className="table-secondary">
                  <td className="ps-4 fw-bold">Total taxes</td>
                  <td className="text-end fw-bold">{syntheseData.totalTaxes.toFixed(2)} €</td>
                  <td className="text-end fw-bold">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalTaxes.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">FR Taux réduit (5.5%)</td>
                  <td className="text-end">{syntheseData.totalTaxes.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalTaxes.toFixed(2)} €</td>
                </tr>
                
                <tr><td colSpan="4" className="bg-white border-0 py-2"></td></tr>
                
                <tr className="table-secondary">
                  <td className="ps-4 fw-bold">Total encaissé</td>
                  <td className="text-end fw-bold">{syntheseData.totalEncaisse.toFixed(2)} €</td>
                  <td className="text-end fw-bold">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalEncaisse.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">Espèces</td>
                  <td className="text-end">{syntheseData.totalEspeces.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalEspeces.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">Payplug</td>
                  <td className="text-end">{syntheseData.totalPayplug.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalPayplug.toFixed(2)} €</td>
                </tr>
                <tr>
                  <td className="ps-4 text-muted">Carte bancaire</td>
                  <td className="text-end">{syntheseData.totalCB.toFixed(2)} €</td>
                  <td className="text-end">0.00 €</td>
                  <td className="text-end pe-4 fw-bold">{syntheseData.totalCB.toFixed(2)} €</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PilotageLayout>
  );
};

export default PilotageSynthese;
